import { Badge, Box, Flex, HStack, IconButton, Stack, Text, Tooltip } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoChevronBack } from 'react-icons/io5';
import { FaChartLine } from 'react-icons/fa6';
import { DtType, ProjectType } from '../../../generated/graphql';
import { HelivanColor } from '../../helivan/HelivanColor';
import { ProjectTabViewOverall } from '../ProjectTabViewOverall';
import { useTour } from '../../Tour/TourContext';
import { DemoInspect } from './DemoInspect';
import { demoTabBridge } from './demoTourBridge';
import {
  DEMO_AGENTS,
  DEMO_ALL_TIMELINE_PERSPECTIVES,
  DEMO_BURN_IN_PERIOD,
  DEMO_CURRENT_PROJECT_ID,
  DEMO_PERSPECTIVES,
  DEMO_SENSITIVITY_STD_DEVS,
  DEMO_TIMELINE_PROJECTS,
  DEMO_TOPIC,
} from './demoWatch';

// A self-contained, read-only sample watch (/projects/demo). It renders the real
// prop-driven plot with curated fake data so the demo tour can teach plot-reading.
// No backend queries, no mutating actions.
export const DemoWatchView = () => {
  const navigate = useNavigate();
  const { maybeAutoStartDemoTour } = useTour();

  const [tab, setTab] = useState<'monitor' | 'inspect'>('monitor');
  // All three agents pre-selected so the timeline + control band render immediately.
  const [selectedTwins, setSelectedTwins] = useState<Set<{ id: string; name: string }>>(
    () => new Set(DEMO_AGENTS)
  );

  // Register the tab setter for the tour, and start the demo tour on first view.
  useEffect(() => {
    demoTabBridge.setTab = setTab;
    maybeAutoStartDemoTour();
    return () => {
      demoTabBridge.setTab = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const TabPill = ({ id, label }: { id: 'monitor' | 'inspect'; label: string }) => (
    <Box
      onClick={() => setTab(id)}
      px={4}
      py={1.5}
      borderRadius="6px"
      bg={tab === id ? 'white' : 'transparent'}
      boxShadow={tab === id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'}
      cursor="pointer"
      transition="all 0.15s ease"
      _hover={{ bg: tab === id ? 'white' : 'rgba(255,255,255,0.5)' }}
    >
      <Text
        fontSize="13px"
        fontWeight={tab === id ? 600 : 500}
        color={tab === id ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid}
      >
        {label}
      </Text>
    </Box>
  );

  return (
    <Stack width="full" height="full" spacing={0} overflow="hidden">
      {/* Header */}
      <Flex
        width="full"
        alignItems="center"
        py="10px"
        px={{ base: 3, md: 5 }}
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
        bg="white"
        gap={3}
        flexShrink={0}
      >
        <IconButton
          aria-label="Back to watches"
          icon={<IoChevronBack size={18} />}
          variant="ghost"
          size="sm"
          color={HelivanColor.text.blueMid}
          onClick={() => navigate('/projects')}
        />
        <FaChartLine size={18} color={HelivanColor.text.helivanBlue} />
        <Text fontSize={{ base: '13px', md: '15px' }} fontWeight={600} color={HelivanColor.text.blueDark} noOfLines={1}>
          {DEMO_TOPIC}
        </Text>
        <Badge colorScheme="purple" borderRadius="full" px={2} fontSize="11px">
          Example · sample data
        </Badge>

        {/* Timeline dots */}
        <HStack
          data-tour="demo-timeline-dots"
          spacing={0}
          flex={1}
          justifyContent="center"
          maxWidth="360px"
          mx={4}
          display={{ base: 'none', md: 'flex' }}
        >
          <Box position="relative" height="28px" width="100%" display="flex" alignItems="center">
            <Box position="absolute" top="50%" left="0" right="0" height="2px" bg={HelivanColor.border.primary} transform="translateY(-50%)" />
            {DEMO_TIMELINE_PROJECTS.map((tp, index) => {
              const isCurrent = index === DEMO_TIMELINE_PROJECTS.length - 1;
              const position = (index / (DEMO_TIMELINE_PROJECTS.length - 1)) * 100;
              return (
                <Box
                  key={tp.id}
                  position="absolute"
                  left={`${position}%`}
                  transform="translateX(-50%)"
                  width={isCurrent ? '12px' : '8px'}
                  height={isCurrent ? '12px' : '8px'}
                  borderRadius="50%"
                  bg={isCurrent ? HelivanColor.text.helivanBlue : 'white'}
                  border="2px solid"
                  borderColor={HelivanColor.text.helivanBlue}
                  zIndex={1}
                />
              );
            })}
          </Box>
        </HStack>
      </Flex>

      {/* Tab pills */}
      <Flex
        width="full"
        px={{ base: 3, md: 5 }}
        py={3}
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
        bg="white"
        flexShrink={0}
      >
        <HStack data-tour="demo-tabs" spacing={1} bg={HelivanColor.background.secondary} p={1} borderRadius="8px">
          <TabPill id="monitor" label="Monitor" />
          <TabPill id="inspect" label="Inspect" />
          <Tooltip label="Steer is coming soon" placement="top" hasArrow>
            <Box px={4} py={1.5} borderRadius="6px" cursor="not-allowed" opacity={0.5}>
              <Text fontSize="13px" fontWeight={500} color={HelivanColor.text.blueMid}>
                Steer
              </Text>
            </Box>
          </Tooltip>
        </HStack>
      </Flex>

      {/* Content */}
      <Flex flex="1" overflowY="auto" bg={HelivanColor.background.secondary} p={{ base: 3, md: 6 }} justifyContent="center">
        {tab === 'monitor' ? (
          <Box data-tour="demo-plot" width="full" display="flex" justifyContent="center">
            <ProjectTabViewOverall
              allTwins={DEMO_AGENTS}
              selectedTwins={selectedTwins}
              setSelectedTwins={setSelectedTwins}
              setDigitalTwinId={() => {}}
              topic={DEMO_TOPIC}
              perspectives={DEMO_PERSPECTIVES}
              subtopics={[]}
              setPlotRef={() => {}}
              digitalTwinType={DtType.Model}
              timelineProjects={DEMO_TIMELINE_PROJECTS}
              currentProjectId={DEMO_CURRENT_PROJECT_ID}
              allTimelinePerspectives={DEMO_ALL_TIMELINE_PERSPECTIVES}
              projectType={ProjectType.Explorable}
              requestTimelineData={() => {}}
              initialTabIndex={0}
              changeDetectionEnabled={true}
              sensitivityStdDevs={DEMO_SENSITIVITY_STD_DEVS}
              burnInPeriod={DEMO_BURN_IN_PERIOD}
            />
          </Box>
        ) : (
          <Box data-tour="demo-inspect" width="full" display="flex" justifyContent="center">
            <DemoInspect />
          </Box>
        )}
      </Flex>
    </Stack>
  );
};
